import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Code, Plus, Copy, Share2, Trash2, Eye, EyeOff, Edit, Clock, 
  FileCode, Terminal, Database, Settings, Check,
  ExternalLink, Lock, Globe, Radio, Users
} from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";
import { categoryAPI, courseAPI } from "@/lib/api";
import { CourseGrouping } from "@/components/CourseGrouping";
import { CategoryManagerButton } from "@/components/CategoryManagerButton";

// Helper: map course title to technology icon URL (Simple Icons CDN) and premium colors
const TECH_INFO_MAP = [
  { keywords: ['ansible'], icon: 'https://cdn.simpleicons.org/ansible/EE0000', color: '#EE0000', name: 'Ansible' },
  { keywords: ['apache', 'httpd', 'web server'], icon: 'https://cdn.simpleicons.org/apache/D22128', color: '#D22128', name: 'Apache' },
  { keywords: ['tomcat'], icon: 'https://cdn.simpleicons.org/apachetomcat/F8DC75', color: '#F8DC75', name: 'Tomcat' },
  { keywords: ['docker'], icon: 'https://cdn.simpleicons.org/docker/2496ED', color: '#2496ED', name: 'Docker' },
  { keywords: ['kubernetes', 'k8s'], icon: 'https://cdn.simpleicons.org/kubernetes/326CE5', color: '#326CE5', name: 'Kubernetes' },
  { keywords: ['jenkins'], icon: 'https://cdn.simpleicons.org/jenkins/D24939', color: '#D24939', name: 'Jenkins' },
  { keywords: ['git', 'gitlab'], icon: 'https://cdn.simpleicons.org/gitlab/FC6D26', color: '#FC6D26', name: 'GitLab' },
  { keywords: ['linux'], icon: 'https://cdn.simpleicons.org/linux/FCC624', color: '#FCC624', name: 'Linux' },
  { keywords: ['postgres', 'postgresql'], icon: 'https://cdn.simpleicons.org/postgresql/4169E1', color: '#4169E1', name: 'PostgreSQL' },
  { keywords: ['zabbix'], icon: 'https://cdn.simpleicons.org/zabbix/D40000', color: '#D40000', name: 'Zabbix' },
  { keywords: ['openshift'], icon: 'https://cdn.simpleicons.org/redhatopenshift/EE0000', color: '#EE0000', name: 'OpenShift' },
  { keywords: ['devops', 'cicd', 'ci/cd'], icon: 'https://cdn.simpleicons.org/azuredevops/0078D7', color: '#0078D7', name: 'DevOps' },
  { keywords: ['python'], icon: 'https://cdn.simpleicons.org/python/3776AB', color: '#3776AB', name: 'Python' },
  { keywords: ['nginx'], icon: 'https://cdn.simpleicons.org/nginx/009639', color: '#009639', name: 'Nginx' },
  { keywords: ['terraform'], icon: 'https://cdn.simpleicons.org/terraform/844FBA', color: '#844FBA', name: 'Terraform' },
  { keywords: ['prometheus'], icon: 'https://cdn.simpleicons.org/prometheus/E6522C', color: '#E6522C', name: 'Prometheus' },
  { keywords: ['grafana'], icon: 'https://cdn.simpleicons.org/grafana/F46800', color: '#F46800', name: 'Grafana' },
  { keywords: ['aws', 'amazon'], icon: 'https://cdn.simpleicons.org/amazonaws/FF9900', color: '#FF9900', name: 'AWS' },
  { keywords: ['azure'], icon: 'https://cdn.simpleicons.org/microsoftazure/0078D4', color: '#0078D4', name: 'Azure' },
  { keywords: ['gcp', 'google cloud'], icon: 'https://cdn.simpleicons.org/googlecloud/4285F4', color: '#4285F4', name: 'GCP' },
  { keywords: ['mysql', 'mariadb'], icon: 'https://cdn.simpleicons.org/mysql/4479A1', color: '#4479A1', name: 'MySQL' },
  { keywords: ['mongodb', 'mongo'], icon: 'https://cdn.simpleicons.org/mongodb/47A248', color: '#47A248', name: 'MongoDB' },
  { keywords: ['redis'], icon: 'https://cdn.simpleicons.org/redis/DC382D', color: '#DC382D', name: 'Redis' },
  { keywords: ['vagrant'], icon: 'https://cdn.simpleicons.org/vagrant/1868F2', color: '#1868F2', name: 'Vagrant' },
  { keywords: ['haproxy'], icon: 'https://cdn.simpleicons.org/haproxy/007DC3', color: '#007DC3', name: 'HAProxy' },
  { keywords: ['nagios'], icon: 'https://cdn.simpleicons.org/nagios/008000', color: '#008000', name: 'Nagios' },
];

function getTechInfo(title) {
  if (!title) return { icon: null, color: '#64748b' };
  const lower = title.toLowerCase();
  for (const entry of TECH_INFO_MAP) {
    if (entry.keywords.some(kw => lower.includes(kw))) {
      return { icon: entry.icon, color: entry.color };
    }
  }
  return { icon: null, color: '#64748b' };
}

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');

// Supported languages with their Prism identifiers
const LANGUAGES = [
  { id: 'yaml', name: 'YAML', icon: FileCode },
  { id: 'bash', name: 'Shell / Bash', icon: Terminal },
  { id: 'python', name: 'Python', icon: FileCode },
  { id: 'javascript', name: 'JavaScript', icon: FileCode },
  { id: 'typescript', name: 'TypeScript', icon: FileCode },
  { id: 'json', name: 'JSON', icon: Database },
  { id: 'sql', name: 'SQL', icon: Database },
  { id: 'dockerfile', name: 'Dockerfile', icon: FileCode },
  { id: 'nginx', name: 'Nginx', icon: Settings },
  { id: 'markup', name: 'HTML/XML', icon: FileCode },
  { id: 'css', name: 'CSS', icon: FileCode },
  { id: 'go', name: 'Go', icon: FileCode },
  { id: 'rust', name: 'Rust', icon: FileCode },
  { id: 'java', name: 'Java', icon: FileCode },
  { id: 'csharp', name: 'C#', icon: FileCode },
  { id: 'php', name: 'PHP', icon: FileCode },
  { id: 'ruby', name: 'Ruby', icon: FileCode },
  { id: 'hcl', name: 'Terraform (HCL)', icon: Settings },
  { id: 'ini', name: 'INI / Ansible', icon: Settings },
  { id: 'markdown', name: 'Markdown', icon: FileCode },
];

// Code Editor Component with syntax highlighting
const CodeEditor = ({ value, onChange, language, readOnly = false }) => {
  return (
    <div className="relative rounded-lg overflow-hidden border bg-[#1e1e1e]">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className="w-full h-[400px] p-4 font-mono text-sm bg-transparent text-gray-100 resize-none focus:outline-none"
        placeholder="// Collez votre code ici..."
        spellCheck="false"
        style={{ 
          tabSize: 2,
          lineHeight: '1.5',
          fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace"
        }}
      />
    </div>
  );
};

// Code Preview with syntax highlighting
const CodePreview = ({ code, language }) => {
  // Map some language IDs to Prism language names
  const prismLanguage = {
    'hcl': 'javascript', // HCL is similar to JS syntax for highlighting
    'ini': 'ini',
    'dockerfile': 'docker',
    'nginx': 'nginx',
  }[language] || language;

  return (
    <div className="rounded-lg overflow-hidden border">
      <Highlight theme={themes.vsDark} code={code || ''} language={prismLanguage}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre 
            className="p-4 overflow-auto text-sm" 
            style={{ 
              ...style, 
              margin: 0,
              minHeight: '200px',
              maxHeight: '500px',
              fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace"
            }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })} className="table-row">
                <span className="table-cell pr-4 text-gray-500 select-none text-right" style={{ minWidth: '2rem' }}>
                  {i + 1}
                </span>
                <span className="table-cell">
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </span>
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
};

export default function CodeSharePage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [copied, setCopied] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [courses, setCourses] = useState([]);

  // Edit snippet dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState(null);
  const [updating, setUpdating] = useState(false);

  // New snippet form
  const [newSnippet, setNewSnippet] = useState({
    title: '',
    code: '',
    language: 'yaml',
    description: '',
    isPublic: true,
    category_id: '',
    course_id: ''
  });

  useEffect(() => {
    loadSnippets();
    categoryAPI.list(workspaceId).then(res => setCategories(res.data || [])).catch(() => {});
    courseAPI.list(workspaceId).then(res => setCourses(res.data || [])).catch(() => {});
  }, [workspaceId]);

  const loadSnippets = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/workspaces/${workspaceId}/codeshare`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSnippets(data);
      }
    } catch (error) {
      console.error('Failed to load snippets:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSnippet = async () => {
    if (!newSnippet.title || !newSnippet.code) {
      toast.error('Titre et code requis');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${workspaceId}/codeshare`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...newSnippet,
            createdBy: user?.id
          })
        }
      );

      if (response.ok) {
        const created = await response.json();
        setSnippets(prev => [created, ...prev]);
        setShowCreateDialog(false);
        setNewSnippet({ title: '', code: '', language: 'yaml', description: '', isPublic: true, category_id: '', course_id: '' });
        toast.success('Snippet créé avec succès');
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      console.error('Failed to create snippet:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const deleteSnippet = async (snippetId) => {
    if (!confirm('Supprimer ce snippet ?')) return;

    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${workspaceId}/codeshare/${snippetId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setSnippets(prev => prev.filter(s => s.id !== snippetId));
        if (selectedSnippet?.id === snippetId) {
          setSelectedSnippet(null);
        }
        toast.success('Snippet supprimé');
      }
    } catch (error) {
      console.error('Failed to delete snippet:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const startEditSnippet = (snippet) => {
    setEditingSnippet({
      id: snippet.id,
      title: snippet.title,
      code: snippet.code,
      language: snippet.language,
      description: snippet.description || '',
      isPublic: snippet.isPublic !== false,
      category_id: snippet.category_id || '',
      course_id: snippet.course_id || '',
      isVisible: snippet.isVisible !== false
    });
    setShowEditDialog(true);
  };

  const updateSnippet = async () => {
    if (!editingSnippet.title || !editingSnippet.code) {
      toast.error('Titre et code requis');
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${workspaceId}/codeshare/${editingSnippet.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: editingSnippet.title,
            code: editingSnippet.code,
            language: editingSnippet.language,
            description: editingSnippet.description || '',
            isPublic: editingSnippet.isPublic,
            isVisible: editingSnippet.isVisible,
            category_id: editingSnippet.category_id || '',
            course_id: editingSnippet.course_id || '',
            createdBy: selectedSnippet?.createdBy
          })
        }
      );

      if (response.ok) {
        const updated = await response.json();
        setSnippets(prev => prev.map(s => s.id === updated.id ? updated : s));
        setSelectedSnippet(updated);
        setShowEditDialog(false);
        toast.success('Snippet mis à jour avec succès');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Failed to update snippet:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const toggleVisibility = async (snippet) => {
    const newVisibility = snippet.isVisible === false ? true : false;
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${workspaceId}/codeshare/${snippet.id}/visibility?isVisible=${newVisibility}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const updated = await response.json();
        setSnippets(prev => prev.map(s => s.id === updated.id ? updated : s));
        setSelectedSnippet(updated);
        toast.success(newVisibility ? 'Snippet visible pour tout le monde' : 'Snippet masqué pour les candidats');
      } else {
        toast.error('Erreur lors du changement de visibilité');
      }
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      toast.error('Erreur lors du changement de visibilité');
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  const copyShareLink = async (snippet) => {
    const shareUrl = `${window.location.origin}/share/code/${snippet.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien de partage copié !');
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  // Navigate to the workspace's live session (same room for everyone)
  const openLiveSession = () => {
    navigate(`/live/${workspaceId}`);
  };

  const getLanguageIcon = (langId) => {
    const lang = LANGUAGES.find(l => l.id === langId);
    return lang?.icon || FileCode;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Category-based grouping
  const hasCategories = categories.length > 0;
  const snippetsByGroup = snippets.reduce((acc, s) => {
    const groupKey = hasCategories ? (s.category_id || "__uncategorized__") : "__all__";
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(s);
    return acc;
  }, {});
  const sortedGroups = (() => {
    if (hasCategories) {
      const result = [];
      categories.forEach(cat => {
        if (snippetsByGroup[cat.id]) {
          result.push({ key: cat.id, label: cat.name, color: cat.color, items: snippetsByGroup[cat.id] });
        }
      });
      if (snippetsByGroup["__uncategorized__"]) {
        result.push({ key: "__uncategorized__", label: "Sans catégorie", color: null, items: snippetsByGroup["__uncategorized__"] });
      }
      return result;
    }
    return [];
  })();

  // Auto-select first tab
  useEffect(() => {
    if (!hasCategories || sortedGroups.length === 0) return;
    if (!sortedGroups.some(g => g.key === activeTab)) {
      setActiveTab(sortedGroups[0].key);
    }
  }, [snippets, categories]);

  const filteredSnippets = hasCategories
    ? (sortedGroups.find(g => g.key === activeTab)?.items || [])
    : snippets;

  const isCreatorOrStaffOf = (s) => {
    if (!s) return false;
    return user?.role === 'admin' || user?.role === 'trainer' || s.createdBy === user?.id;
  };

  const isCreatorOrStaff = isCreatorOrStaffOf(selectedSnippet);

  const visibleSnippets = filteredSnippets.filter(s => {
    // 1. Basic visibility of the snippet itself
    const snippetSelfVisible = s.isVisible !== false;
    
    // 2. Course-based visibility for candidates
    let courseVisible = true;
    const isCandidate = user?.role === 'candidate' || user?.role === 'student';
    if (isCandidate && s.course_id) {
      courseVisible = courses.some(c => c.id === s.course_id);
    }
    
    if (snippetSelfVisible && courseVisible) return true;
    return isCreatorOrStaffOf(s);
  });

  return (
    <WorkspaceLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Code className="h-6 w-6" />
              CodeShare
            </h1>
            <p className="text-muted-foreground">
              Partagez vos snippets de code avec coloration syntaxique
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Category Manager */}
            <CategoryManagerButton />

            {/* Live Session Button */}
            <Button
              variant="outline"
              onClick={openLiveSession}
              className="bg-green-600 hover:bg-green-700 text-white border-green-700"
              data-testid="create-live-session-btn"
            >
              <Radio className="h-4 w-4 mr-2" />
              Session Live
            </Button>

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-snippet-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouveau Snippet
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer un nouveau snippet</DialogTitle>
                <DialogDescription>
                  Partagez du code avec coloration syntaxique
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Titre *</Label>
                    <Input
                      value={newSnippet.title}
                      onChange={(e) => setNewSnippet(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Mon script de déploiement"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Langage</Label>
                    <Select
                      value={newSnippet.language}
                      onValueChange={(v) => setNewSnippet(prev => ({ ...prev, language: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map(lang => (
                          <SelectItem key={lang.id} value={lang.id}>
                            <span className="flex items-center gap-2">
                              <lang.icon className="h-4 w-4" />
                              {lang.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description (optionnel)</Label>
                  <Input
                    value={newSnippet.description}
                    onChange={(e) => setNewSnippet(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Script pour automatiser le déploiement Kubernetes"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Code *</Label>
                  <CodeEditor
                    value={newSnippet.code}
                    onChange={(v) => setNewSnippet(prev => ({ ...prev, code: v }))}
                    language={newSnippet.language}
                  />
                </div>

                {categories.length > 0 && (
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select
                      value={newSnippet.category_id || "none"}
                      onValueChange={(v) => setNewSnippet(prev => ({ ...prev, category_id: v === "none" ? "" : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Aucune catégorie" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune catégorie</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded" style={{ backgroundColor: cat.color }} />
                              {cat.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {courses.length > 0 && (
                  <div className="space-y-2">
                    <Label>Cours rattaché</Label>
                    <Select
                      value={newSnippet.course_id || "none"}
                      onValueChange={(v) => setNewSnippet(prev => ({ ...prev, course_id: v === "none" ? "" : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Aucun cours" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun cours</SelectItem>
                        {courses.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={newSnippet.isPublic}
                    onChange={(e) => setNewSnippet(prev => ({ ...prev, isPublic: e.target.checked }))}
                    className="rounded"
                  />
                  <Label htmlFor="isPublic" className="flex items-center gap-2 cursor-pointer">
                    {newSnippet.isPublic ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    {newSnippet.isPublic ? 'Public (accessible via lien)' : 'Privé (workspace uniquement)'}
                  </Label>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Annuler
                  </Button>
                  <Button onClick={createSnippet} disabled={creating}>
                    {creating ? 'Création...' : 'Créer le snippet'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Modifier le snippet</DialogTitle>
                <DialogDescription>
                  Modifiez les informations, le code et la visibilité de votre snippet
                </DialogDescription>
              </DialogHeader>
              
              {editingSnippet && (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Titre *</Label>
                      <Input
                        value={editingSnippet.title}
                        onChange={(e) => setEditingSnippet(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Mon script de déploiement"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Langage</Label>
                      <Select
                        value={editingSnippet.language}
                        onValueChange={(v) => setEditingSnippet(prev => ({ ...prev, language: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LANGUAGES.map(lang => (
                            <SelectItem key={lang.id} value={lang.id}>
                              <span className="flex items-center gap-2">
                                <lang.icon className="h-4 w-4" />
                                {lang.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description (optionnel)</Label>
                    <Input
                      value={editingSnippet.description}
                      onChange={(e) => setEditingSnippet(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Script pour automatiser le déploiement Kubernetes"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Code *</Label>
                    <CodeEditor
                      value={editingSnippet.code}
                      onChange={(v) => setEditingSnippet(prev => ({ ...prev, code: v }))}
                      language={editingSnippet.language}
                    />
                  </div>

                  {categories.length > 0 && (
                    <div className="space-y-2">
                      <Label>Catégorie</Label>
                      <Select
                        value={editingSnippet.category_id || "none"}
                        onValueChange={(v) => setEditingSnippet(prev => ({ ...prev, category_id: v === "none" ? "" : v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Aucune catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucune catégorie</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded" style={{ backgroundColor: cat.color }} />
                                {cat.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {courses.length > 0 && (
                    <div className="space-y-2">
                      <Label>Cours rattaché</Label>
                      <Select
                        value={editingSnippet.course_id || "none"}
                        onValueChange={(v) => setEditingSnippet(prev => ({ ...prev, course_id: v === "none" ? "" : v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Aucun cours" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun cours</SelectItem>
                          {courses.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex items-center gap-6 py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="editIsPublic"
                        checked={editingSnippet.isPublic}
                        onChange={(e) => setEditingSnippet(prev => ({ ...prev, isPublic: e.target.checked }))}
                        className="rounded"
                      />
                      <Label htmlFor="editIsPublic" className="flex items-center gap-2 cursor-pointer select-none">
                        {editingSnippet.isPublic ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        {editingSnippet.isPublic ? 'Public (accessible via lien)' : 'Privé (workspace uniquement)'}
                      </Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="editIsVisible"
                        checked={editingSnippet.isVisible}
                        onChange={(e) => setEditingSnippet(prev => ({ ...prev, isVisible: e.target.checked }))}
                        className="rounded"
                      />
                      <Label htmlFor="editIsVisible" className="flex items-center gap-2 cursor-pointer select-none">
                        {editingSnippet.isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        {editingSnippet.isVisible ? 'Visible pour tous' : 'Masqué (réservé formateur/admin)'}
                      </Label>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                      Annuler
                    </Button>
                    <Button onClick={updateSnippet} disabled={updating}>
                      {updating ? 'Mise à jour...' : 'Mettre à jour'}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Category Tabs */}
        {hasCategories && sortedGroups.length > 0 && (
          <div className="flex gap-2 flex-wrap pb-2 border-b border-border overflow-x-auto">
            {sortedGroups.map(group => (
              <button
                key={group.key}
                onClick={() => setActiveTab(group.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === group.key
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {group.color && (
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                )}
                {group.label}
                <span className="ml-1 bg-background/50 text-xs px-1.5 py-0.5 rounded-full">{group.items.length}</span>
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Snippets List */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Mes Snippets</CardTitle>
                <CardDescription>
                  {visibleSnippets.length} snippet{visibleSnippets.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Chargement...
                  </div>
                ) : visibleSnippets.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Code className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun snippet</p>
                    <p className="text-sm">Créez votre premier snippet !</p>
                  </div>
                ) : (() => {
                  const renderSnippetItem = (snippet, index, courseColor) => {
                    const LangIcon = getLanguageIcon(snippet.language);
                    const isSelected = selectedSnippet?.id === snippet.id;
                    return (
                      <div
                        key={snippet.id}
                        onClick={() => setSelectedSnippet(snippet)}
                        className={`relative pl-4 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/5 shadow-sm scale-[1.01]' 
                            : 'hover:border-primary/30 hover:bg-muted/30 hover:scale-[1.005]'
                        } ${snippet.isVisible === false ? 'opacity-65 border-dashed bg-muted/10' : ''}`}
                        style={isSelected && courseColor ? { borderColor: `${courseColor}88`, backgroundColor: `${courseColor}08` } : {}}
                      >
                        {courseColor && (
                          <div 
                            className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-md transition-all" 
                            style={{ backgroundColor: courseColor }}
                          />
                        )}
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <LangIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="font-medium truncate">{snippet.title}</span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 ml-2">
                            {snippet.isVisible === false && (
                              <Badge className="text-[10px] gap-1 px-1.5 py-0 bg-orange-600/10 text-orange-500 hover:bg-orange-600/20 border-orange-500/20 shadow-none font-medium">
                                <EyeOff className="h-2.5 w-2.5" />
                                Masqué
                              </Badge>
                            )}
                            <Badge variant={snippet.isPublic ? "default" : "secondary"}>
                              {snippet.isPublic ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                            </Badge>
                          </div>
                        </div>
                        {snippet.description && (
                          <p className="text-sm text-muted-foreground mt-1 truncate">
                            {snippet.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {formatDate(snippet.createdAt)}
                        </div>
                      </div>
                    );
                  };

                  return (
                    <CourseGrouping
                      items={visibleSnippets}
                      courses={courses}
                      renderItem={renderSnippetItem}
                      emptyLabel="Sans cours"
                      icon={Code}
                    />
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Code Preview */}
          <div className="lg:col-span-2">
            {selectedSnippet ? (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {(() => {
                          const LangIcon = getLanguageIcon(selectedSnippet.language);
                          return <LangIcon className="h-5 w-5" />;
                        })()}
                        {selectedSnippet.title}
                      </CardTitle>
                      {selectedSnippet.description && (
                        <CardDescription className="mt-1">
                          {selectedSnippet.description}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyCode(selectedSnippet.code)}
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        <span className="ml-2">{copied ? 'Copié !' : 'Copier'}</span>
                      </Button>
                      {selectedSnippet.isPublic && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyShareLink(selectedSnippet)}
                        >
                          <Share2 className="h-4 w-4" />
                          <span className="ml-2">Partager</span>
                        </Button>
                      )}
                      {isCreatorOrStaff && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleVisibility(selectedSnippet)}
                            className="gap-2"
                          >
                            {selectedSnippet.isVisible === false ? (
                              <>
                                <Eye className="h-4 w-4 text-green-500" />
                                <span className="text-green-500">Afficher</span>
                              </>
                            ) : (
                              <>
                                <EyeOff className="h-4 w-4 text-orange-500" />
                                <span className="text-orange-500">Masquer</span>
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEditSnippet(selectedSnippet)}
                            className="gap-2"
                          >
                            <Edit className="h-4 w-4" />
                            <span>Modifier</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteSnippet(selectedSnippet.id)}
                            className="text-destructive hover:text-destructive gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <Badge variant="outline">
                      {LANGUAGES.find(l => l.id === selectedSnippet.language)?.name || selectedSnippet.language}
                    </Badge>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(selectedSnippet.createdAt)}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <CodePreview 
                    code={selectedSnippet.code} 
                    language={selectedSnippet.language} 
                  />
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full min-h-[400px] flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Sélectionnez un snippet pour voir le code</p>
                  <p className="text-sm">ou créez-en un nouveau</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
